/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IAllowableFreightData } from './allowable-freight.data';
import { IAllowableFreightProps } from './allowable-freight.props.autogenerated';
import { Cart, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { AllowableFreightStatusType } from './utils/AllowableFreightStatusEnum';
import { AllowableFreightExtensionPropertyKeys } from './utils/AllowableFreightExtensionPropertyKeys';
import { CommercePropertyValueEnum } from './utils/CommercePropertyValueEnum';
import { AllowableFreightProgressBarClassEnum } from './utils/AllowableFreightProgressBarEnum';
import { reaction } from 'mobx';

export interface IAllowableFreightViewProps extends IAllowableFreightProps<IAllowableFreightData> {
    state: AllowableFreightState;
}

export interface AllowableFreightState {
    containsOil: boolean;
    oversizedItem: boolean;
    mustShipLTL: boolean;
    limitReached: boolean;
    cartFreight: number;
    sliderColourClass: string;
    errorMessage: string | undefined;
    maxLimit: number;
    isUnitCount: boolean;
}

/**
 *
 * AllowableFreight component
 * @extends {React.PureComponent<IAllowableFreightProps<IAllowableFreightData>>}
 */
class AllowableFreight extends React.PureComponent<IAllowableFreightProps<IAllowableFreightData>, AllowableFreightState> {
    constructor(props: IAllowableFreightProps<IAllowableFreightData>) {
        super(props);
        this.state = {
            containsOil: false,
            oversizedItem: false,
            mustShipLTL: false,
            limitReached: false,
            cartFreight: 0,
            sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
            errorMessage: undefined,
            maxLimit: 0,
            isUnitCount: false
        };
    }
    public componentDidMount() {
        if (this.props.data.cart.result?.cart) {
            this.updateAllowableFreight(this.props.data.cart.result.cart);
        }

        reaction(
            () => this.props.data.cart.result?.cart,
            () => {
                // console.log('Reaction fired', this.props.data.cart.result?.cart);
                this.updateAllowableFreight(this.props.data.cart.result!.cart);
            }
        );
    }

    public updateAllowableFreight = (cart: Cart) => {
        //Check the allowable freight because it has the lowest priority, followed by the item filter.
        this.checkAllowableFreightLimit(cart);
        Object.keys(AllowableFreightStatusType).forEach(k => {
            // eslint-disable-next-line security/detect-object-injection
            this.applyGlobalState(cart, AllowableFreightStatusType[k]);
        });

        // this.applyFreightSliderColor();
    };

    // private applyFreightSliderColor = () => {
    //     if (this.state.mustShipLTL) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Red });
    //         return;
    //     } else if (this.state.containsOil || this.state.oversizedItem) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Yellow });
    //         return;
    //     } else if (this.state.limitReached) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Green });
    //         return;
    //     }
    // };

    private checkAllowableFreightLimit = (cart: Cart) => {
        const maxAllowableFreight =
            this.getExtensionPropertyValueByKey(
                cart.ExtensionProperties,
                AllowableFreightExtensionPropertyKeys.AllowableFreightThresholdTotalKey,
                CommercePropertyValueEnum.decimal
            ) || 0;

        const isUnitCount = this.getExtensionPropertyValueByKey(
            cart.ExtensionProperties,
            AllowableFreightExtensionPropertyKeys.AllowableFreightIsUnitCountKey,
            CommercePropertyValueEnum.boolean
        );

        const cartFreight =
            this.getExtensionPropertyValueByKey(
                cart.ExtensionProperties,
                AllowableFreightExtensionPropertyKeys.AllowableFreightThresholdAmountKey,
                CommercePropertyValueEnum.decimal
            ) || 0;

        this.setState({ cartFreight: parseInt(cartFreight as string, 10), maxLimit: parseInt(maxAllowableFreight as string, 10) });
        if (cartFreight >= maxAllowableFreight) {
            this.setState({
                limitReached: true,
                sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
                errorMessage: this.props.resources.freightAllowanceLimitMessage,
                isUnitCount: (isUnitCount as boolean) || false
            });
        } else {
            this.setState({
                limitReached: false,
                sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
                errorMessage: undefined,
                isUnitCount: (isUnitCount as boolean) || false
            });
        }
    };

    private getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    private applyGlobalState = (cart: Cart, key: AllowableFreightStatusType) => {
        const hasStatus =
            cart.CartLines?.filter(
                f =>
                    this.getExtensionPropertyValueByKey(
                        f.ExtensionProperties,
                        AllowableFreightExtensionPropertyKeys.AllowableFreightStatusType,
                        CommercePropertyValueEnum.string
                    ) === key
            )?.length || false;
        if (hasStatus) {
            switch (key) {
                //Highest priority
                case AllowableFreightStatusType.LTL:
                    this.setState({
                        mustShipLTL: true,
                        errorMessage: this.props.resources.mustShipLTLMessage
                    });
                    break;
                case AllowableFreightStatusType.Oil:
                    this.setState({
                        containsOil: true,
                        errorMessage: this.props.resources.containsOilMessage
                    });
                    break;
                case AllowableFreightStatusType.Oversized:
                    this.setState({
                        oversizedItem: true,
                        errorMessage: this.props.resources.overSizedItemMessage
                    });
                    break;
            }
        }
    };

    public render(): JSX.Element | null {
        const viewProps: IAllowableFreightViewProps = {
            ...this.props,
            state: this.state
        };
        return this.props.renderView(viewProps);
    }
}

export default AllowableFreight;
